import * as React from 'react'
import { ArrowLeft, ArrowRight, CaretDown, X } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { type BadgeColor } from './badge'
import { softSurface, softText, midRing } from './badge-colors'
import { ColorDot } from './color-dot'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './dropdown-menu'

/**
 * StageTracker — the per-deal pipeline control on a deal header. It mirrors the
 * app's sales-stage bar: a **back cluster** (a caret menu of earlier stages +
 * `◂ Move backward`), the **current stage** rendered as a pill tinted with that
 * stage's own color and a handshake glyph, and a **forward cluster**
 * (`Move forward ▸` + a caret menu of later stages, with closed outcomes under a
 * "Close out" group). Each cluster is one bordered shell of borderless segments
 * split by a hairline divider — the same split-control pattern as `SortMenu`
 * (not `ButtonGroup`, which can't flow size/join styling through a menu trigger).
 *
 *   <StageTracker
 *     stages={STAGES}              // pipeline + terminal (Won / Churn), any order
 *     currentStageId="target"      // or null for "No stage yet"
 *     onChange={(id) => moveDealTo(id)}
 *     onClear={() => clearStage()}  // enables "Clear stage" / the ✕ affordance
 *   />
 *
 * A cluster is shown only when it has a real move — the control never renders a
 * disabled step. `Move backward` appears with a previous stage; at the first
 * stage the back cluster collapses to a ✕ clear (when `onClear` is set) or hides.
 * `Move forward` advances while there's a next stage; at the last stage the
 * forward cluster becomes a single "Close out" dropdown (the terminal outcomes);
 * with no stage selected `Move forward` enters the first stage and the back
 * cluster is hidden. When the deal is **already closed out** (current stage is
 * terminal) the stepping clusters are replaced by a single caret menu that
 * offers the other outcomes ("Change outcome") and the pipeline stages
 * ("Re-open to a stage") — a closed deal has no "forward". `disabled` hides all
 * clusters, leaving just the stage pill.
 * When `onClear` is set the back caret menu also carries a "Clear stage — No
 * stage yet" item.
 *
 * By default the terminal outcomes live in the forward menu; pass
 * `terminalStyle="buttons"` to instead show them as an always-visible segmented
 * group (Won / Lost / Churn) for one-click close-out (legacy sales-stage-bar
 * parity), using each terminal stage's `shortName` to stay compact.
 *
 * `stages` is structurally the same shape as `OrganizeStagesModal`'s
 * `SalesStageItem` (`id` / `name` / `color` / `terminal`), so one pipeline drives
 * both. Controlled only: render `currentStageId` from your own state.
 */

export interface StageTrackerStage {
  /** Stable unique id — what `onChange` reports and `currentStageId` matches. */
  id: string
  /** Stage name shown on the pill and in the caret menus. */
  name: string
  /** Badge-palette color — tints the current-stage pill and the menu dots. */
  color: BadgeColor
  /**
   * Closed-outcome stage (Closed Won / Lost / Churn): grouped under "Close out"
   * in the forward menu and excluded from the stepping pipeline.
   */
  terminal?: boolean
  /**
   * Optional pill glyph shown when this stage is current. Pipeline stages carry
   * no glyph by default (the name + color tint carry it); pass one to give a
   * closed outcome its own icon (a celebration for Closed Won, a circled ✕ for
   * Closed Lost, a slashed circle for Churn). Sized to the pill automatically;
   * menu rows still use the color dot.
   */
  icon?: React.ReactNode
  /**
   * Short label for the always-visible terminal button group (see
   * `terminalStyle="buttons"`) — e.g. "Won" for a "Closed Won" stage, so the
   * group stays compact. Falls back to `name`. Unused by the caret menus.
   */
  shortName?: string
}

export interface StageTrackerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Every stage — pipeline + terminal, in pipeline order. */
  stages: StageTrackerStage[]
  /** The deal's current stage id, or `null` for "No stage yet". */
  currentStageId: string | null
  /** Fired with the chosen stage id (step or jump). */
  onChange: (stageId: string) => void
  /** Enables clearing back to "No stage yet" (back caret item + ✕ affordance). */
  onClear?: () => void
  /** Forward step-button label. */
  advanceLabel?: string
  /** Back step-button label. */
  backLabel?: string
  /** Label above the closed-outcome group in the forward menu. */
  closeOutLabel?: string
  /**
   * Label above the *other* terminal outcomes in the menu shown when the deal is
   * already closed out (e.g. Closed Won → Closed Lost / Churn).
   */
  changeOutcomeLabel?: string
  /**
   * Label above the pipeline stages in the closed-out menu — the re-open options
   * that move a closed deal back into an active stage.
   */
  reopenLabel?: string
  /**
   * How the terminal (closed-outcome) stages are surfaced:
   * - `menu` (default) — folded into the forward caret menu under a "Close out"
   *   group, and shown as a single "Close out" dropdown at the last stage. The
   *   compact, self-adapting behavior.
   * - `buttons` — an always-visible segmented button group (Won / Lost / Churn)
   *   after the forward cluster, mirroring the app's legacy sales-stage bar for
   *   one-click close-out. Use `shortName` on each terminal stage to keep the
   *   group tight. Falls back to `menu` when `compact` (no room for the group).
   */
  terminalStyle?: 'menu' | 'buttons'
  /** Text shown in the pill when no stage is selected. */
  noStageLabel?: string
  /** Hide both step clusters, leaving just the current-stage pill. */
  disabled?: boolean
  /** Row scale (default `sm`). */
  size?: 'sm' | 'md'
  /**
   * Compact variant for tight surfaces (deal cards, dense tables): the step
   * buttons collapse to icon-only arrows (their labels move to `aria-label`) and
   * the close-out button to just its caret, so the control shrinks to the pill +
   * two small clusters.
   */
  compact?: boolean
}

const SIZING = {
  sm: { h: 'h-8', text: 'text-sm', pad: 'px-3', caretPad: 'px-2.5', icon: 'size-3.5' },
  md: { h: 'h-9', text: 'text-sm', pad: 'px-3.5', caretPad: 'px-3', icon: 'size-4' },
} as const

/** One bordered cluster shell (rounded, clips inner segment corners). */
const clusterShell =
  'inline-flex shrink-0 items-center overflow-hidden rounded-md border border-fbui-gray-300 bg-fbui-base-white'

/** A borderless segment inside a cluster shell. */
const segmentBase =
  'relative flex h-full items-center gap-1.5 font-medium text-fbui-gray-800 outline-none transition-colors hover:bg-fbui-gray-100 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand disabled:cursor-not-allowed disabled:text-fbui-text-disabled disabled:hover:bg-transparent'

/** Hairline divider between two segments. */
const SegmentDivider = () => (
  <span aria-hidden="true" className="w-px self-stretch bg-fbui-gray-200" />
)

/** A stage row inside a caret menu — color dot + name. */
const StageMenuItem = ({
  stage,
  onSelect,
}: {
  stage: StageTrackerStage
  onSelect: (id: string) => void
}) => (
  <DropdownMenuItem onSelect={() => onSelect(stage.id)}>
    <ColorDot color={stage.color} className="mr-2" />
    {stage.name}
  </DropdownMenuItem>
)

export const StageTracker = React.forwardRef<HTMLDivElement, StageTrackerProps>(
  (
    {
      stages,
      currentStageId,
      onChange,
      onClear,
      advanceLabel = 'Move forward',
      backLabel = 'Move backward',
      closeOutLabel = 'Close out',
      changeOutcomeLabel = 'Change outcome',
      reopenLabel = 'Re-open to a stage',
      noStageLabel = 'No stage yet',
      disabled = false,
      size = 'sm',
      compact = false,
      terminalStyle = 'menu',
      className,
      ...props
    },
    ref
  ) => {
    const s = SIZING[size]
    // Icon-only step/close-out buttons in compact mode use the caret padding.
    const stepPad = compact ? s.caretPad : cn(s.text, s.pad)

    /** A bare caret segment that opens its cluster's menu (back + forward share it). */
    const caretTrigger = (ariaLabel: string) => (
      <DropdownMenuTrigger
        type="button"
        aria-label={ariaLabel}
        className={cn(segmentBase, s.caretPad)}
      >
        <CaretDown className={s.icon} aria-hidden="true" />
      </DropdownMenuTrigger>
    )
    const pipeline = stages.filter((st) => !st.terminal)
    const terminal = stages.filter((st) => st.terminal)
    const current = stages.find((st) => st.id === currentStageId) ?? null

    // A deal already in a closed outcome — there is no "forward" from here; the
    // stepping clusters give way to a single change-outcome / re-open menu.
    const currentIsTerminal = current != null && !!current.terminal
    const otherTerminals = terminal.filter((st) => st.id !== currentStageId)

    // The always-visible terminal button group (legacy sales-stage-bar parity).
    // Compact has no room, so it falls back to the in-menu "Close out" group; a
    // closed-out deal uses the change-outcome menu instead, not the group.
    const showTerminalGroup =
      terminalStyle === 'buttons' &&
      terminal.length > 0 &&
      !disabled &&
      !compact &&
      !currentIsTerminal
    // When the group is visible it owns close-out, so the forward menu drops its
    // "Close out" group and the last-stage forward cluster (a dead dropdown then).
    const terminalInMenu = terminal.length > 0 && !showTerminalGroup

    // Stepping walks the non-terminal pipeline. index === -1 covers both "no
    // stage yet" and a terminal stage — from either, forward enters the pipeline.
    const index = pipeline.findIndex((st) => st.id === currentStageId)
    const prevStage = index > 0 ? pipeline[index - 1] : null
    const nextStage =
      index === -1
        ? (pipeline[0] ?? null)
        : index < pipeline.length - 1
          ? pipeline[index + 1]
          : null

    const earlierOptions = index > 0 ? pipeline.slice(0, index) : []
    const laterOptions = index === -1 ? pipeline : pipeline.slice(index + 1)

    // Clearing only makes sense when there's a stage to clear.
    const canClear = !disabled && onClear != null && current != null

    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-3', className)}
        {...props}
      >
        {/* Back cluster — shown only when there's a real backward move. With a
            previous stage: [caret (earlier stages) | Move backward]. At the first
            stage: just a ✕ clear, when `onClear` is set. Otherwise hidden — we
            never render a disabled step. Hidden entirely while `disabled`. */}
        {!disabled &&
          !currentIsTerminal &&
          current != null &&
          (prevStage ? (
            <DropdownMenu>
              <div className={cn(clusterShell, s.h)}>
                {caretTrigger('Jump to an earlier stage')}
                <SegmentDivider />
                <button
                  type="button"
                  onClick={() => onChange(prevStage.id)}
                  aria-label={compact ? backLabel : undefined}
                  className={cn(segmentBase, stepPad)}
                >
                  <ArrowLeft className={s.icon} aria-hidden="true" />
                  {!compact && backLabel}
                </button>
              </div>
              <DropdownMenuContent align="start" className="min-w-56">
                {earlierOptions.map((stage) => (
                  <StageMenuItem key={stage.id} stage={stage} onSelect={onChange} />
                ))}
                {canClear && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={onClear}
                      icon={<X aria-hidden="true" />}
                    >
                      Clear stage
                      <span className="ml-1 text-fbui-gray-400">— {noStageLabel}</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : canClear ? (
            <div className={cn(clusterShell, s.h)}>
              <button
                type="button"
                onClick={onClear}
                aria-label="Clear stage"
                className={cn(segmentBase, s.caretPad)}
              >
                <X className={s.icon} aria-hidden="true" />
              </button>
            </div>
          ) : null)}

        {/* Current stage — a pill tinted with the stage's own color. */}
        <div
          className={cn(
            'flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium ring-1 ring-inset transition-colors',
            s.h,
            current
              ? cn(softSurface[current.color], softText[current.color], midRing[current.color])
              : 'bg-fbui-gray-100 text-fbui-gray-500 ring-fbui-gray-300'
          )}
        >
          {current?.icon && (
            <span
              className="flex shrink-0 items-center [&>svg]:size-3.5"
              aria-hidden="true"
            >
              {current.icon}
            </span>
          )}
          <span className="truncate">{current?.name ?? noStageLabel}</span>
        </div>

        {/* Forward cluster — a "Move forward" split while there's a next stage;
            at the last stage a single "Close out" dropdown (the only forward move
            left); otherwise hidden — never a dead disabled button. Hidden while
            `disabled` or when the deal is already closed out (terminal mode owns
            the trailing control). */}
        {!disabled &&
          !currentIsTerminal &&
          (nextStage ? (
            <DropdownMenu>
              <div className={cn(clusterShell, s.h)}>
                <button
                  type="button"
                  onClick={() => onChange(nextStage.id)}
                  aria-label={compact ? advanceLabel : undefined}
                  className={cn(segmentBase, stepPad)}
                >
                  {!compact && advanceLabel}
                  <ArrowRight className={s.icon} aria-hidden="true" />
                </button>
                <SegmentDivider />
                {caretTrigger(
                  index === -1 ? 'Choose a stage' : 'Jump to a later stage'
                )}
              </div>
              <DropdownMenuContent align="end" className="min-w-56">
                {laterOptions.map((stage) => (
                  <StageMenuItem key={stage.id} stage={stage} onSelect={onChange} />
                ))}
                {terminalInMenu && (
                  <>
                    {laterOptions.length > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel>{closeOutLabel}</DropdownMenuLabel>
                    {terminal.map((stage) => (
                      <StageMenuItem key={stage.id} stage={stage} onSelect={onChange} />
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : terminalInMenu ? (
            <DropdownMenu>
              <div className={cn(clusterShell, s.h)}>
                <DropdownMenuTrigger
                  type="button"
                  aria-label={compact ? closeOutLabel : 'Close out deal'}
                  className={cn(segmentBase, stepPad)}
                >
                  {!compact && closeOutLabel}
                  <CaretDown className={s.icon} aria-hidden="true" />
                </DropdownMenuTrigger>
              </div>
              <DropdownMenuContent align="end" className="min-w-56">
                {terminal.map((stage) => (
                  <StageMenuItem key={stage.id} stage={stage} onSelect={onChange} />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null)}

        {/* Always-visible terminal group — one bordered shell of borderless
            segments (Won / Lost / Churn), sitting after the forward cluster for
            one-click close-out (legacy sales-stage-bar parity). The flex-1
            current-stage pill pushes this to the right edge. */}
        {showTerminalGroup && (
          <div className={cn(clusterShell, s.h)}>
            {terminal.map((stage, i) => (
              <React.Fragment key={stage.id}>
                {i > 0 && <SegmentDivider />}
                {/* The group only renders while the deal is NOT closed out (a
                    closed deal uses the change-outcome menu), so these are always
                    unselected quick-actions — no active/pressed state needed. */}
                <button
                  type="button"
                  onClick={() => onChange(stage.id)}
                  className={cn(segmentBase, s.text, s.pad)}
                >
                  {stage.shortName ?? stage.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Closed-out menu — a deal in a terminal stage has no "forward"; this
            single caret offers the other outcomes ("Change outcome") and the
            pipeline stages ("Re-open to a stage"), plus Clear. Replaces the
            stepping clusters entirely. */}
        {!disabled && currentIsTerminal && (
          <DropdownMenu>
            <div className={cn(clusterShell, s.h)}>
              {caretTrigger('Move out of this stage')}
            </div>
            <DropdownMenuContent align="end" className="min-w-56">
              {otherTerminals.length > 0 && (
                <>
                  <DropdownMenuLabel>{changeOutcomeLabel}</DropdownMenuLabel>
                  {otherTerminals.map((stage) => (
                    <StageMenuItem key={stage.id} stage={stage} onSelect={onChange} />
                  ))}
                </>
              )}
              {pipeline.length > 0 && (
                <>
                  {otherTerminals.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel>{reopenLabel}</DropdownMenuLabel>
                  {pipeline.map((stage) => (
                    <StageMenuItem key={stage.id} stage={stage} onSelect={onChange} />
                  ))}
                </>
              )}
              {canClear && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={onClear}
                    icon={<X aria-hidden="true" />}
                  >
                    Clear stage
                    <span className="ml-1 text-fbui-gray-400">
                      — {noStageLabel}
                    </span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    )
  }
)
StageTracker.displayName = 'StageTracker'
